import { DataFilterExtension } from "@deck.gl/extensions";
import { Color } from "@deck.gl/core";
import { Polygon, MultiPolygon, Point, Feature, feature } from "@turf/helpers";
import { isNumber } from "lodash";

import { showError } from "src/js/stores/user/actions/addNotification";
import { ENVIRONMENT } from "src/js/util/environment";

import { PlanningApplicationGeoJsonLayer } from "react-migration/domains/planning/map/layers/PlanningApplicationGeoJsonLayer/PlanningApplicationGeoJsonLayer";
import { getPointRadius } from "react-migration/domains/planning/map/layers/PlanningApplicationGeoJsonLayer/accessors";
import { isDefined } from "react-migration/lib/util/isDefined";
import {
  PlanningApplicationFeature,
  PlanningApplicationFeatureProps,
  PlanningClassification,
} from "react-migration/domains/planning/types";
import {
  ClusteredViewportSearchMapLayer,
  ClusteredViewportSearchMapLayerProps,
  ParsedClusterPoint,
} from "react-migration/lib/map/layers/ClusteredViewportSearchMapLayer";

type ClusteredPlanningApplicationDocument = Pick<
  PlanningApplicationFeatureProps,
  "id" | "classification" | "date_received" | "size" | "is_minor" | "status_derived"
> & {
  geometry: Polygon | MultiPolygon | Point;
};

type ClusteredPlanningApplicationFeatureProps = Pick<
  ClusteredPlanningApplicationDocument,
  "id" | "classification" | "date_received" | "size" | "is_minor"
> & {
  has_boundary: boolean;
  layerName: "boundaries" | "dots";
};

type PlanningApplicationClusteredViewportSearchMapLayerProps = Omit<
  ClusteredViewportSearchMapLayerProps<
    ClusteredPlanningApplicationDocument,
    ClusteredPlanningApplicationFeatureProps,
    PlanningApplicationsClusteredAggregations
  >,
  | "FeatureLayerClass"
  | "parseOpenSearchFeatures"
  | "getClusterFillColor"
  | "getClusterRadius"
  | "onFetchClusterError"
  | "renderFeatureLayers"
  | "url"
> & {
  showBoundaries: boolean;
};

type PlanningApplicationsClusteredAggregations = {
  classifications: PlanningClassification[];
};

type PlanningApplicationCluster = ParsedClusterPoint<
  ClusteredPlanningApplicationDocument,
  PlanningApplicationsClusteredAggregations
>;

export class PlanningApplicationClusteredViewportSearchMapLayer extends ClusteredViewportSearchMapLayer<
  ClusteredPlanningApplicationDocument,
  ClusteredPlanningApplicationFeatureProps,
  PlanningApplicationsClusteredAggregations
> {
  static layerName = "PlanningApplicationClusteredViewportSearchMapLayer";

  constructor(props: PlanningApplicationClusteredViewportSearchMapLayerProps) {
    super({
      ...props,
      url: `${ENVIRONMENT.SUPPORTING_SERVICE_URL}/planning-search-layer/clustered-viewport`,
      parseOpenSearchFeatures,
      getClusterFillColor,
      getClusterLineColor: [255, 255, 255],
      getClusterRadius: 25,
      renderFeatureLayers: (features) => {
        const { showBoundaries } = props;

        return [
          new PlanningApplicationGeoJsonLayer(
            this.getSubLayerProps({
              id: `${this.props.id}-exploded-dots`,
              data: features,
              pickable: true,
              updateTriggers: {
                getFilterValue: [props.showBoundaries],
              },
              extensions: [new DataFilterExtension({ filterSize: 1 })],
              getFilterValue: (d: { properties: ClusteredPlanningApplicationFeatureProps }) => {
                if (showBoundaries && d.properties.layerName === "dots" && d.properties.has_boundary) {
                  // hide points with boundaries
                  return [-1];
                } else if (!showBoundaries && d.properties.layerName === "boundaries") {
                  // hide boundaries if boundary mode isn't active
                  return [-1];
                }

                return [1];
              },
              filterRange: [0, 1],
            })
          ),
        ];
      },
      onFetchClusterError: (error) => {
        console.error(error);

        showError({
          id: `clustered-viewport-layer_${props.id}_search-error`,
          title: "Error searching planning applications",
          description: "Failed to load planning applications",
          duration: 10_000,
        });
      },
    });
  }
}

// TODO typing for esDocument, possibly zod
export function parseOpenSearchFeatures(
  esDocument: any
): Feature<Polygon | MultiPolygon, ClusteredPlanningApplicationFeatureProps>[] {
  const properties = {
    id: esDocument.id,
    classification: esDocument.classification,
    date_received: new Date(esDocument.date_received).getTime(),
    size: isNumber(esDocument.num_dwellings) ? esDocument.num_dwellings : 0,
    is_minor: esDocument.commercial_category === "MINOR",
    has_boundary: esDocument.has_boundary,
  };

  const radius = getPointRadius({ properties } as PlanningApplicationFeature);

  const a = [
    feature(esDocument.location, {
      ...properties,
      layerName: "dots" as const,
      __radius: radius,
    }),
    properties.has_boundary
      ? feature(esDocument.geometry, {
          ...properties,
          layerName: "boundaries" as const,
        })
      : null,
  ].filter(isDefined);

  return a;
}

function getClusterFillColor({ classifications }: PlanningApplicationCluster): Color {
  const classificationsSansMixedUse = classifications.filter((c) => c !== PlanningClassification.MIXED_USE);

  // Treat MIXED_USE as COMMERCIAL or RESIDENTIAL
  if (classificationsSansMixedUse.length === 1) {
    const [remainingClass] = classificationsSansMixedUse;

    if (remainingClass === PlanningClassification.RESIDENTIAL) {
      return [218, 69, 108];
    } else if (remainingClass === PlanningClassification.COMMERCIAL) {
      return [138, 107, 233];
    }
  }

  if (classifications.length === 1) {
    const [classification] = classifications;

    if (classification === PlanningClassification.MIXED_USE) {
      return [255, 54, 101];
    } else if (classification === PlanningClassification.OTHER) {
      return [49, 179, 223];
    }
  }

  return [78, 102, 132];
}
